import { MeasurementName } from '@quromedical/models';
import { getDisplayDateTime } from 'core/display';
import { FormCard, SkeletonProvider, RowConfig } from 'design-system';
import { DeviceApi } from 'integration/aggregate';
import React from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

interface StatusCardProps {
  deviceId: string;
}

const deviceApi = new DeviceApi();

const wearStatusLabel: Record<number, string> = {
  0: strings.LabelBandStatusNotWearing,
  1: strings.LabelBandStatusWearing,
};

const deviceStatusLabel: Record<number, string> = {
  1: strings.LabelBandStatusDeviceOk,
  2: strings.LabelBandStatusDeviceNotWearing,
  3: strings.LabelBandStatusDeviceDisconnected,
  4: strings.LabelBandStatusDeviceCharging,
  5: strings.LabelBandStatusDeviceLowBattery,
  6: strings.LabelBandStatusDeviceCriticalLowBattery,
};

const sensorQualityLabel: Record<number, string> = {
  0: strings.LabelBandStatusDeviceSensorOk,
  1: strings.LabelBandStatusDeviceSensorLow,
};

export const BandStatusCard: React.FC<StatusCardProps> = ({ deviceId }) => {
  const statusFetcher = () => deviceApi.getBandStatus(deviceId);
  const swrKey = `trpc/devices/${deviceId}/band-status`;
  const status = useSWR(swrKey, statusFetcher);

  const loading = status.isValidating;

  const batteryStatus = status.data?.find(m => m.measurementName === MeasurementName.Battery);
  const wearStatus = status.data?.find(m => m.measurementName === MeasurementName.WearStatus);
  const deviceStatus = status.data?.find(m => m.measurementName === MeasurementName.DeviceStatus);
  const sensorQuality = status.data?.find(
    m => m.measurementName === MeasurementName.SensorLowQuality
  );

  const batteryDate = batteryStatus?.ts
    ? getDisplayDateTime(batteryStatus?.ts)
    : strings.DisplayNotFound;
  const wearDate = wearStatus?.ts ? getDisplayDateTime(wearStatus.ts) : strings.DisplayNotFound;
  const deviceDate = deviceStatus?.ts
    ? getDisplayDateTime(deviceStatus.ts)
    : strings.DisplayNotFound;
  const sensorDate = sensorQuality?.ts
    ? getDisplayDateTime(sensorQuality.ts)
    : strings.DisplayNotFound;

  const rows: RowConfig[] = [
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelBandStatusBattery,
        },
        {
          type: 'text',
          label: batteryStatus ? `${batteryStatus.value} %` : strings.LabelBandStatusNotFound,
        },
        {
          type: 'text',
          label: batteryDate ? batteryDate : strings.DisplayNotFound,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelBandStatusWear,
        },
        {
          type: 'text',
          label: wearStatus ? wearStatusLabel[wearStatus.value] : strings.LabelBandStatusNotFound,
        },
        {
          type: 'text',
          label: wearDate ? wearDate : strings.DisplayNotFound,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelBandStatusDevice,
        },
        {
          type: 'text',
          label: deviceStatus
            ? deviceStatusLabel[deviceStatus.value]
            : strings.LabelBandStatusNotFound,
        },
        {
          type: 'text',
          label: deviceDate ? deviceDate : strings.DisplayNotFound,
        },
      ],
    },

    {
      fields: sensorQuality
        ? [
            {
              type: 'text',
              label: strings.LabelBandStatusDeviceSensor,
            },
            {
              type: 'text',
              label: sensorQualityLabel[sensorQuality.value],
            },
            {
              type: 'text',
              label: sensorDate ? sensorDate : strings.DisplayNotFound,
            },
          ]
        : [],
    },
  ];

  return (
    <>
      <SkeletonProvider loading={loading}>
        <FormCard rows={rows} title={strings.LabelBandStatusTitle} />
      </SkeletonProvider>
    </>
  );
};
